/**
 * =====================================================================================
 * SERVICIO DE DASHBOARD - dashboard.service.js
 * =====================================================================================
 *
 * Servicio para obtener KPIs, estadísticas y datos analíticos del sistema.
 * Conecta con el DashboardController del backend.
 *
 * ENDPOINTS DISPONIBLES:
 * ----------------------
 * GET /api/dashboard/kpis                  - KPIs principales (totales, promedios)
 * GET /api/dashboard/ventas-por-dia        - Ventas por día para gráfica de tendencia
 * GET /api/dashboard/productos-mas-vendidos - TOP N productos más vendidos
 * GET /api/dashboard/ventas-por-categoria  - Ventas agrupadas por categoría
 *
 * ESTRUCTURA DE RESPUESTAS (DTOs del Backend):
 * --------------------------------------------
 * DashboardKPIs: TotalFacturas, TotalVentas, PromedioVenta, FacturasAprobadas,
 *                FacturasPendientes, FacturasRechazadas, CrecimientoVentas
 * VentasPorDia: Fecha, CantidadFacturas, TotalVentas
 * ProductoMasVendido: ProductoId, NombreProducto, CantidadVendida, TotalVentas
 * VentasPorCategoria: CategoriaId, NombreCategoria, TotalVentas, CantidadProductos
 *
 * =====================================================================================
 */

import api from './api'

const dashboardService = {
  /**
   * Obtiene los KPIs principales del dashboard
   *
   * @param {string|null} fechaInicio - Fecha inicio del período (formato: YYYY-MM-DD)
   * @param {string|null} fechaFin - Fecha fin del período (formato: YYYY-MM-DD)
   * @returns {Promise<Object>} KPIs del dashboard
   *
   * Respuesta esperada:
   * {
   *   totalFacturas: number,
   *   totalVentas: number,
   *   promedioVenta: number,
   *   facturasAprobadas: number,
   *   facturasPendientes: number,
   *   facturasRechazadas: number,
   *   crecimientoVentas: number (porcentaje)
   * }
   */
  async getKPIs(fechaInicio = null, fechaFin = null, sucursalId = null, fechaAnteriorInicio = null, fechaAnteriorFin = null, ambiente = null) {
    const params = {}

    // Enviar fechas como UTC directamente (sin conversión de timezone local)
    if (fechaInicio) {
      params.fechaInicio = fechaInicio + 'T00:00:00Z'
    }
    if (fechaFin) {
      params.fechaFin = fechaFin + 'T23:59:59Z'
    }
    if (sucursalId) {
      params.sucursalId = sucursalId
    }
    if (fechaAnteriorInicio) {
      params.fechaAnteriorInicio = fechaAnteriorInicio + 'T00:00:00Z'
    }
    if (fechaAnteriorFin) {
      params.fechaAnteriorFin = fechaAnteriorFin + 'T23:59:59Z'
    }
    if (ambiente) params.ambiente = ambiente

    const response = await api.get('/dashboard/kpis', { params })
    return response.data
  },

  /**
   * Obtiene las ventas por día para gráfica de tendencia
   *
   * @param {number} dias - Cantidad de días hacia atrás (default: 30)
   * @returns {Promise<Array>} Lista de ventas por día
   *
   * Respuesta esperada:
   * [
   *   { fecha: "2025-12-01", cantidadFacturas: 15, totalVentas: 4500.00 },
   *   { fecha: "2025-12-02", cantidadFacturas: 12, totalVentas: 3800.00 },
   *   ...
   * ]
   */
  async getVentasPorDia(dias = 30, sucursalId = null, ambiente = null) {
    const params = { dias }
    if (sucursalId) {
      params.sucursalId = sucursalId
    }
    if (ambiente) params.ambiente = ambiente
    const response = await api.get('/dashboard/ventas-por-dia', { params })
    return response.data
  },

  /**
   * Obtiene el ranking de productos más vendidos
   *
   * @param {number} top - Cantidad de productos a obtener (default: 10)
   * @param {number|null} sucursalId - ID de sucursal para filtrar
   * @param {string|null} fechaInicio - Fecha inicio (YYYY-MM-DD)
   * @param {string|null} fechaFin - Fecha fin (YYYY-MM-DD)
   * @returns {Promise<Array>} Lista de productos más vendidos
   */
  async getProductosMasVendidos(top = 10, sucursalId = null, fechaInicio = null, fechaFin = null, ambiente = null) {
    const params = { top }
    if (sucursalId) {
      params.sucursalId = sucursalId
    }
    if (fechaInicio) {
      params.fechaInicio = fechaInicio
    }
    if (fechaFin) {
      params.fechaFin = fechaFin
    }
    if (ambiente) params.ambiente = ambiente
    const response = await api.get('/dashboard/productos-mas-vendidos', { params })
    return response.data
  },

  /**
   * Obtiene las ventas agrupadas por categoría
   *
   * @returns {Promise<Array>} Lista de ventas por categoría
   *
   * Respuesta esperada:
   * [
   *   { categoriaId: 1, nombreCategoria: "Electrónicos", totalVentas: 45000.00, cantidadProductos: 25 },
   *   { categoriaId: 2, nombreCategoria: "Ropa", totalVentas: 32000.00, cantidadProductos: 18 },
   *   ...
   * ]
   */
  async getVentasPorCategoria(sucursalId = null, fechaInicio = null, fechaFin = null, ambiente = null) {
    const params = {}
    if (sucursalId) params.sucursalId = sucursalId
    if (fechaInicio) {
      params.fechaInicio = fechaInicio + 'T00:00:00Z'
    }
    if (fechaFin) {
      params.fechaFin = fechaFin + 'T23:59:59Z'
    }
    if (ambiente) params.ambiente = ambiente
    const response = await api.get('/dashboard/ventas-por-categoria', { params })
    return response.data
  },

  /**
   * Obtiene el dashboard del cajero (ventas realizadas por el usuario)
   *
   * @param {string|null} fechaDesde - Fecha inicio del período (formato: YYYY-MM-DD)
   * @param {string|null} fechaHasta - Fecha fin del período (formato: YYYY-MM-DD)
   * @returns {Promise<Object>} Dashboard del cajero
   *
   * Respuesta esperada:
   * {
   *   totalFacturas: number,
   *   montoTotalVendido: number,
   *   promedioVenta: number,
   *   ventasPorDia: [{ fecha, cantidadFacturas, montoTotal }, ...]
   * }
   */
  async getDashboardCajero(fechaDesde = null, fechaHasta = null, ambiente = null) {
    const params = {}

    if (fechaDesde) {
      params.fechaDesde = fechaDesde + 'T00:00:00Z'
    }
    if (fechaHasta) {
      params.fechaHasta = fechaHasta + 'T23:59:59Z'
    }
    if (ambiente) params.ambiente = ambiente

    const response = await api.get('/dashboard/cajero', { params })
    return response.data
  },

  /**
   * Obtiene todos los datos del dashboard en una sola llamada
   * Útil para cargar todo el dashboard de una vez
   *
   * @param {Object} opciones - Opciones de carga
   * @param {string|null} opciones.fechaInicio - Fecha inicio para KPIs
   * @param {string|null} opciones.fechaFin - Fecha fin para KPIs
   * @param {number} opciones.diasVentas - Días para gráfica de ventas (default: 30)
   * @param {number} opciones.topProductos - Cantidad de productos top (default: 10)
   * @returns {Promise<Object>} Todos los datos del dashboard
   */
  async getDashboardCompleto(opciones = {}) {
    const {
      fechaInicio = null,
      fechaFin = null,
      diasVentas = 30,
      topProductos = 10,
      sucursalId = null,
      ambiente = null
    } = opciones

    // Ejecutar todas las peticiones en paralelo para mejor rendimiento
    const [kpis, ventasPorDia, productosMasVendidos, ventasPorCategoria] = await Promise.all([
      this.getKPIs(fechaInicio, fechaFin, sucursalId, null, null, ambiente),
      this.getVentasPorDia(diasVentas, sucursalId, ambiente),
      this.getProductosMasVendidos(topProductos, sucursalId, fechaInicio, fechaFin, ambiente),
      this.getVentasPorCategoria(sucursalId, fechaInicio, fechaFin, ambiente)
    ])

    return {
      kpis,
      ventasPorDia,
      productosMasVendidos,
      ventasPorCategoria
    }
  }
}

export default dashboardService
